const BLOCK_PATTERN = /\bblock\s*0*(\d{1,3})\b/i;

const normalizeText = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const toTitleCase = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const toBlockName = (number) => `Block ${String(number).padStart(3, '0')}`;

const toBlockId = (name) => normalizeText(name)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const parseBlockFromStation = (station, fallbackOrder = 0) => {
  const candidates = [
    station?.locationDescription,
    station?.sectionName,
    station?.sectionId,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const match = String(candidate).match(BLOCK_PATTERN);
    if (match) {
      const number = Number(match[1]);
      if (Number.isFinite(number) && number > 0) {
        const name = toBlockName(number);
        return {
          id: `block-${String(number).padStart(3, '0')}`,
          name,
          order: number,
        };
      }
    }
  }

  const fallbackName = normalizeText(station?.sectionName || station?.sectionId || station?.locationDescription);
  if (fallbackName) {
    const readable = toTitleCase(fallbackName);
    return {
      id: toBlockId(readable) || `block-fallback-${fallbackOrder + 1}`,
      name: readable,
      order: Number.MAX_SAFE_INTEGER,
    };
  }

  const generatedName = toBlockName(fallbackOrder + 1);
  return {
    id: `block-fallback-${String(fallbackOrder + 1).padStart(3, '0')}`,
    name: generatedName,
    order: Number.MAX_SAFE_INTEGER,
  };
};

const sortByCode = (a, b) => {
  const aCode = String(a?.stationCode || '');
  const bCode = String(b?.stationCode || '');
  return aCode.localeCompare(bCode);
};

export const groupStationsForList = ({ stations = [], farms = [] } = {}) => {
  const farmOrder = new Map((farms || []).map((farm, index) => [farm.id, index]));
  const grouped = new Map();

  stations.forEach((station, index) => {
    const farmId = station?.farmId || 'unknown-farm';
    const farmName = station?.farmName || farms.find((farm) => farm.id === farmId)?.name || 'Unknown Farm';
    const blockMeta = parseBlockFromStation(station, index);

    if (!grouped.has(farmId)) {
      grouped.set(farmId, {
        farm: { id: farmId, name: farmName },
        blocks: new Map(),
      });
    }

    const farmGroup = grouped.get(farmId);
    if (!farmGroup.blocks.has(blockMeta.id)) {
      farmGroup.blocks.set(blockMeta.id, {
        id: blockMeta.id,
        name: blockMeta.name,
        order: blockMeta.order,
        stations: [],
      });
    }

    farmGroup.blocks.get(blockMeta.id).stations.push(station);
  });

  return Array.from(grouped.values())
    .map(({ farm, blocks }) => ({
      farm,
      blocks: Array.from(blocks.values())
        .map((block) => ({
          ...block,
          stations: block.stations.slice().sort(sortByCode),
        }))
        .sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order;
          return a.name.localeCompare(b.name);
        }),
    }))
    .sort((a, b) => {
      const aOrder = farmOrder.has(a.farm.id) ? farmOrder.get(a.farm.id) : Number.MAX_SAFE_INTEGER;
      const bOrder = farmOrder.has(b.farm.id) ? farmOrder.get(b.farm.id) : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.farm.name.localeCompare(b.farm.name);
    });
};
